<script setup lang="ts">
import { computed } from "vue";
import { TuNavbarFooter } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import useTranslation from "@Composables/useTranslation.ts";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";
import useNavigationButtons, { availableButtons } from "@Components/overhauling/application-view/useNavigationButtons";
import XOverlay from "@Modules/shared/store/XOverlay";

//
// Setup
const authStore = XAuthStore();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const { __ } = useTranslation();
const overlay = XOverlay();

const {
  homeButton,
  helpButton,
  notificationButton,
  kioskButton,
  domainSettingsButton,
  shoppingCartButton,
} = useNavigationButtons();

//
// Data
const isMobileView = breakpoints.smallerOrEqual("xl");

const footerLinks = [
  { label: __("footer.short_terms"), to: { name: "terms" }, testClass: "_terms" },
  { label: __("footer.legal_link_group.privacy_policy"), to: { name: "privacy" }, testClass: "_privacy" },
  { label: __("footer.short_accessibility"), to: { name: "accessibility" }, testClass: "_accessibility" },
];

//
// Computed
const navbarOptions = computed(() => {
  const buttons = [
    homeButton.value,
    domainSettingsButton.value,
    helpButton.value,
    {
      ...shoppingCartButton.value,
      as: "button",
    },
    notificationButton.value,
  ];

  if (authStore.user.isDeveloper) {
    buttons.push(kioskButton.value);
  }

  return buttons;
});

// Method

function onFooterOptionClicked(optionId: string) {
  if (optionId === availableButtons.SHOPPING_CART) {
    overlay.showShoppingCartPopover();

    return;
  }

  if (optionId === availableButtons.NOTIFICATION) {
    overlay.showNotificationsPopover();

    return;
  }
}

//
// Exposed Test Vars
defineExpose({ navbarOptions });
</script>

<template>
  <div class="teams-application-footer">
    <TuNavbarFooter
      v-if="isMobileView"
      id="footer_section"
      class="teams-application-footer__navbar"
      :options="navbarOptions"
      @option-clicked="onFooterOptionClicked"
    />

    <div class="v2-footer">
      <span class="v2-footer__copyright _copyright">{{ __("footer.short_copyright") }}</span>

      <div
        v-for="link in footerLinks"
        :key="link.to.name"
        class="v2-footer__item"
      >
        <span
          class="v2-footer__separator"
          aria-hidden="true"
        />

        <router-link
          :to="link.to"
          :class="['v2-footer__link', link.testClass]"
        >
          {{ link.label }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;

.v2-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: spacing.$xxxs;
  padding: spacing.$xxxs spacing.$s;
  background: color.$accent-primary;

  &__item {
    display: contents;
  }

  &__copyright,
  &__link {
    color: color.$background-overlay;
    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$base-line-height;
  }

  &__link {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__separator {
    flex-shrink: 0;
    width: 5px;
    height: 5px;
    border-radius: border.$maximum-radius;
    background: color.$background-overlay;
  }

  @media (max-width: 575.98px) {
    gap: spacing.$xs-5;
    padding: spacing.$xs-5 spacing.$xxxs;
  }
}
</style>
